/**
 * FR8-I1 — Briefs review stage (mock 06): draft creative briefs per
 * (angle × format) form a review GATE the user may skip («Generate all» is
 * available immediately — FP-4 confirm-not-form). Per-card «✎» edits replace
 * the generated draft (no silent merge); video drafts land in the FR5
 * storyboard brief stage pre-filled after generation.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { StyledTextField } from '@modules/ui-components';
import type { AdCreativeItem } from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_BRIEFS as BR } from '../../selectors';
import { INSIGHT_CATEGORY_COLOR, parseInsightPath } from '../../services/AdCreativeInsightContextClient';
import type { AdCreativeApiClient, AdCreativeBriefDraft } from '../../services/AdCreativeApiClient';

interface BriefsStepProps {
  projectId: string;
  client: AdCreativeApiClient;
  briefId: string;
  generationId: string;
  /** Refresh the workspace after generation starts (items poll picks tasks up). */
  onGenerated: () => void;
  /** FR8-I2: open a created VIDEO item in the storyboard brief stage. */
  onOpenStoryboard: (itemId: string) => void;
}

export function BriefsStep({
  projectId,
  client,
  briefId,
  generationId,
  onGenerated,
  onOpenStoryboard,
}: BriefsStepProps): React.ReactElement {
  const [drafts, setDrafts] = useState<AdCreativeBriefDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editConcept, setEditConcept] = useState('');
  const [editBeats, setEditBeats] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    const response = await client.getBriefGeneration(projectId, generationId);
    if (response.success && response.data) {
      setDrafts(response.data.drafts);
    } else {
      setError(response.error || 'Failed to load draft briefs');
    }
  }, [client, projectId, generationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const startEdit = (draft: AdCreativeBriefDraft) => {
    setEditingId(draft.id);
    setEditConcept(draft.data.concept);
    setEditBeats(draft.data.beats.map((beat) => beat.text));
  };

  const saveEdit = async (draft: AdCreativeBriefDraft) => {
    setBusy(draft.id);
    setError(null);
    try {
      const response = await client.updateBriefDrafts({
        projectId,
        generationId,
        drafts: [{
          id: draft.id,
          // The edited draft REPLACES the generated one (no silent merge).
          data: {
            ...draft.data,
            concept: editConcept.trim() || draft.data.concept,
            beats: editBeats.map((text, index) => ({
              ...(draft.data.beats[index] ?? { label: `Beat ${index + 1}` }),
              text,
            })),
          },
        }],
      });
      if (!response.success) throw new Error(response.error || 'Failed to save the draft');
      setEditingId(null);
      await refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setBusy(null);
    }
  };

  /** FR8-I2: after a VIDEO draft generates, land in the storyboard brief stage pre-filled. */
  const landVideoDraft = async (draft: AdCreativeBriefDraft): Promise<void> => {
    const deadline = Date.now() + 120_000;
    while (Date.now() < deadline) {
      const items = await client.listItems(projectId, { briefId, limit: 100 });
      const created = (items.data?.items ?? []).find((item: AdCreativeItem) =>
        item.templateId === draft.templateId
        && (item as unknown as { angleId?: string }).angleId === draft.angleId);
      if (created) {
        // FR10-I3: the draft lands WHOLE — concept + adapted beats + scene
        // grounding rows (the backend also seeds these at generation time;
        // this pass covers items created before the seed shipped).
        await client.videoPlanOps(projectId, created.id, {
          op: 'updateBrief',
          concept: draft.data.concept,
          beats: draft.data.beats,
          insightMapping: draft.data.beats
            .filter((beat) => beat.sourcePath)
            .map((beat) => ({ role: beat.label, sourcePath: beat.sourcePath, label: beat.text })),
        } as never).catch(() => undefined);
        onOpenStoryboard(created.id);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 4_000));
    }
  };

  const generate = async (draftIds: string[], navigateVideo?: AdCreativeBriefDraft) => {
    setBusy(draftIds.length === 1 ? draftIds[0] : 'all');
    setError(null);
    try {
      const response = await client.generateFromDrafts({ projectId, generationId, draftIds });
      if (!response.success) throw new Error(response.error || 'Failed to start generation');
      onGenerated();
      await refresh();
      if (navigateVideo) await landVideoDraft(navigateVideo);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setBusy(null);
    }
  };

  if (!drafts) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }} data-testid={BR.stage}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={28} />}
      </Stack>
    );
  }

  const pending = drafts.filter((draft) => draft.status !== 'generated');

  return (
    <Box data-testid={BR.stage}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Draft creative briefs</Typography>
        <Typography variant="caption" color="text.secondary" data-testid={BR.counter}>
          {`${drafts.length} drafts · ${drafts.length - pending.length} generated`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={busy === 'all' ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
          data-testid={BR.generateAll}
          disabled={busy !== null || pending.length === 0}
          onClick={() => void generate(pending.map((draft) => draft.id))}
        >
          Generate all →
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fill, minmax(340px, 1fr))' }, gap: 1.5 }}>
        {drafts.map((draft) => {
          const editing = editingId === draft.id;
          return (
            <Box
              key={draft.id}
              data-testid={BR.card(draft.id)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
            >
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                {draft.data.frameworkName && (
                  <Chip size="small" color="primary" variant="outlined" label={draft.data.frameworkName} sx={{ height: 20, fontSize: 11 }} />
                )}
                <Chip size="small" variant="outlined" label={draft.templateName ?? draft.templateId} sx={{ height: 20, fontSize: 11 }} />
                <Chip
                  size="small"
                  color={draft.mediaKind === 'video' ? 'secondary' : 'default'}
                  label={draft.mediaKind}
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Stack>

              {editing ? (
                <Stack spacing={1}>
                  <StyledTextField
                    label="Concept"
                    value={editConcept}
                    onChange={(value) => setEditConcept(String(value))}
                    multiline
                    minRows={2}
                    fullWidth
                    data-testid={BR.cardConcept(draft.id)}
                  />
                  {editBeats.map((text, index) => (
                    <StyledTextField
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      label={draft.data.beats[index]?.label ?? `Beat ${index + 1}`}
                      value={text}
                      onChange={(value) => setEditBeats((current) => current.map((entry, i) => (i === index ? String(value) : entry)))}
                      size="small"
                      fullWidth
                      data-testid={BR.beatField(draft.id, index)}
                    />
                  ))}
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      data-testid={BR.cardSave(draft.id)}
                      disabled={busy !== null}
                      onClick={() => void saveEdit(draft)}
                    >
                      Save
                    </Button>
                    <Button size="small" data-testid={BR.cardCancel(draft.id)} onClick={() => setEditingId(null)}>Cancel</Button>
                  </Stack>
                </Stack>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {draft.data.concept}
                  </Typography>
                  {draft.data.beats.map((beat, index) => {
                    const parsed = beat.sourcePath ? parseInsightPath(beat.sourcePath) : null;
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <Stack key={index} direction="row" spacing={0.5} alignItems="center" sx={{ pl: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }} noWrap>
                          {`${index + 1}. ${beat.text}`}
                        </Typography>
                        {parsed && (
                          <Chip
                            size="small"
                            color={INSIGHT_CATEGORY_COLOR[parsed.category] ?? 'default'}
                            label={parsed.category}
                            sx={{ height: 16, fontSize: 10, flexShrink: 0 }}
                          />
                        )}
                      </Stack>
                    );
                  })}
                  {(draft.warnings ?? []).map((warning) => (
                    <Typography key={warning} variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }} data-testid={BR.cardWarning(draft.id)}>
                      ⚠ {warning}
                    </Typography>
                  ))}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                    <Button
                      size="small"
                      variant="outlined"
                      data-testid={BR.cardEdit(draft.id)}
                      disabled={busy !== null || draft.status === 'generated'}
                      onClick={() => startEdit(draft)}
                    >
                      ✎ Edit
                    </Button>
                    {draft.status === 'generated' ? (
                      <Typography variant="caption" color="success.main" data-testid={BR.cardGenerated(draft.id)}>
                        ✓ generated
                      </Typography>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        data-testid={BR.cardGenerate(draft.id)}
                        disabled={busy !== null}
                        startIcon={busy === draft.id ? <CircularProgress size={12} /> : undefined}
                        onClick={() => void generate([draft.id], draft.mediaKind === 'video' ? draft : undefined)}
                      >
                        {draft.mediaKind === 'video' ? 'Generate → scenes' : 'Generate this one'}
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
